<template>
  <div class="crud-container">
    <div class="button-container">
      <button @click="openCreateDialog" v-if="authStore?.getRole === 'Admin'" class="create-btn">Create Invoice</button>
      <select v-model="selectedYear" class="year-dropdown">
      <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
      </select>
    </div>

    <!-- Data Grid -->
    <table class="crud-table" v-if="items.length">
      <thead>
        <tr>
          <th>Invoice ID</th>
          <th>Invoice Date</th>
          <th>Customer Name</th>
          <th>Items</th>
          <th>Amount payable</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items" :key="item._id">
          <td>{{ item._id }}</td>
          <td>{{item.billDate}}</td>
          <td>{{ item.name }}</td>
          <td v-html="item.products"></td>
          <td>{{ item.amountPayable }}</td>
          <td>
            <button v-if="authStore?.getRole === 'Admin'" @click="openEditDialog(item)" class="edit-btn">Edit</button>
            <button v-if="authStore?.getRole === 'Admin'" @click="deleteItem(item._id)"
              class="delete-btn">Delete</button>
            <button @click="printInvoice(item)" class="print-btn">Print</button>
            <!-- <button @click="sendInvoiceEmail(item)" class="email-btn">Send Email</button> -->
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination Controls -->
    <div class="pagination">
      <button :disabled="page === 1" @click="changePage(page - 1)">Previous</button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button :disabled="page === totalPages" @click="changePage(page + 1)">Next</button>
    </div>

    <!-- Create/Edit Dialog -->
    <div v-if="dialog" class="dialog">
      <div class="dialog-content">
        <h3>{{ isEdit ? 'Edit Invoice' : 'Create Invoice' }}</h3>

        <!-- Company Selection -->
        <div class="form-group">
          <label for="company">Select Company</label>
          <select id="company" :disabled="isEdit" v-model="itemForm.companyId" @change="updateAdminInfo(itemForm.companyId)">
        <option v-for="company in companies" :key="company._id" :value="company._id">
          {{ company.name }}
        </option>
          </select>
        </div>

        <!-- Bill Date -->
        <div class="form-group">
          <label for="billDate">Bill Date</label>
          <input id="billDate" type="date" v-model="itemForm.billDate" />
        </div>

        <!-- User Selection -->
        <div class="form-group">
          <label for="user">Select User</label>
          <select id="user" v-model="itemForm.userId">
        <option v-for="user in users" :key="user._id" :value="user._id">
          {{ user.name }}
        </option>
          </select>
        </div>

        <!-- Product List -->
        <div class="form-group">
          <label for="product">Add Product</label>
          <div class="product-selection">
        <select id="product" v-model="selectedProduct">
          <option v-for="product in products" :key="product._id" :value="product._id">
        INR.{{ product.amount }} - {{ product.name }}
          </option>
        </select>
        <input type="number" v-model="selectedQuantity" placeholder="Quantity" min="1" />
        <button @click="addProductToList">Add</button>
        <br/>
          </div>
          <ul class="product-list">
        <div v-for="(product, index) in itemForm.items" :key="index">
          {{ product.name }} - {{ product.quantity }}
          <button @click="removeProductFromList(index)">Remove</button>
        </div>
          </ul>
        </div>
        <br/>
        <!-- Tax Type -->
        <div class="form-group">
          <label style="display: flex; align-items: center; gap: 10px;">
            Tax Type:
          </label>
          <div style="display: flex; align-items: center; gap: 10px;">
            <label><input type="radio" v-model="itemForm.taxType" value="GST" /> GST</label>
            <label><input type="radio" v-model="itemForm.taxType" value="PAN" /> PAN</label>
            <label><input type="radio" v-model="itemForm.taxType" value="None" /> NONE</label>
          </div>
        </div>
        
        <!-- Additional Options -->
        <div class="form-group">
          <label>Additional Options:</label>
          <div style="display: flex; gap: 10px; flex-wrap: wrap;">
            <label><input type="checkbox" v-model="itemForm.isTaxable" @change.stop="updateTotalAmount" /> Is Taxable</label>
            <label><input type="checkbox" v-model="itemForm.isProforma" /> Is Proforma</label>
            <label><input type="checkbox" v-model="itemForm.isPaid" /> Is Paid</label>
            <label><input type="checkbox" v-model="itemForm.addSignature" /> Add Signature</label>
            <label><input type="checkbox" v-model="itemForm.isConsolidated" /> Is Consolidated</label>
            <label><input type="checkbox" v-model="itemForm.isNotes" /> Notes</label>
          </div>
        </div>
        <br/>
        <!-- Amount Details -->
        <div class="form-group">
          <label>Total Amount: INR. {{ itemForm.total }}</label>
          <label v-if="itemForm.isTaxable">Tax Amount (18%): INR. {{ itemForm.taxAmount }}</label>
          <label>Amount Payable: INR. {{ itemForm.amountPayable }}</label>
        </div>

        <!-- Actions -->
        <div class="dialog-actions">
          <button @click="dialog = false" class="cancel-btn">Cancel</button>
          <button @click="saveItem" class="save-btn">{{ isEdit ? 'Save' : 'Create' }}</button>
        </div>
  </div>
  </div>  
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import instance from '@/axios'
import { useAuthStore } from '@/stores/authStore'

export default defineComponent({
  setup() {
    const authStore = useAuthStore()
    const items = ref<any[]>([]);
    const users = ref<any[]>([]);
    const selectedProduct = ref(0);
    const selectedQuantity = ref(0);
    const companies = ref<any[]>([]);
    const adminInfo = ref({ _id: 0, name: '', address: '', address1: '', mail: '', mobile: '', notes: '', bankAccountName: '', bankAccountNumber: '', bankName: '', ifscCode: '', panNumber: '', gstNumber: '' });
    const products = ref<any[]>([]);
    const dialog = ref(false);
    const isEdit = ref(false);
    const itemForm = ref({
      _id: 0,
      userId: null,
      items: [{ id: 100, name: '', amount: 0, quantity: 0 }], // Array to hold multiple selected products
      total: 0,
      amountPayable: 0,
      taxAmount: 0,
      taxType: '',
      isTaxable: false,
      companyId: 0,
      isProforma: false,
      isNotes:false,
      isPaid:false,
      billDate: new Date(),
      addSignature: false,
      isConsolidated: false
    });
    const route = useRoute();
    // Pagination properties
    const page = ref(1);
    const totalItems = ref(0);
    const pageSize = ref(5);
    const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

    const selectedYear = ref(new Date().getFullYear());
    const availableYears = ref<number[]>([]);

    onMounted(() => {
      const currentYear = new Date().getFullYear();
      // Generate a list of years from 5 years ago to the current year
      availableYears.value = Array.from({ length: 6 }, (_, i) => currentYear - i);
    });

    watch(selectedYear, async () => {
      page.value = 1; 
      await fetchItems(); // Refetch items when the selected year changes
    });

    const updateTotalAmount = () => {
      // Calculate the total amount based on selected products
      let total = 0;
      itemForm.value.items.forEach((item: any) => {
        const product = products.value.find((product) => product._id === item.id);
        if (product) {
          total += Number(product.amount) * Number(item.quantity);
        }
      });
      itemForm.value.total = total;
      itemForm.value.taxAmount = itemForm.value.isTaxable ? total * 0.18 : 0;
      itemForm.value.amountPayable = (itemForm.value.total + itemForm.value.taxAmount);
    };

    const addProductToList = () => {
      const product = products.value.find((product) => product._id === selectedProduct.value);
      if (product) {
        itemForm.value.items.push({ id: product._id, name: product.name, amount: product.amount, quantity: selectedQuantity.value });
        updateTotalAmount();
      }
    };

    const removeProductFromList = (index: number) => {
      itemForm.value.items.splice(index, 1);
      updateTotalAmount();
    };

    const updateAdminInfo = async (itemId: number) => {
      adminInfo.value = await companies.value.find(x => x._id == itemId);
    };

    const fetchItems = async () => {
      // Make API call with pagination parameters
      
      const routeParams = route.params;
      let endpoint = '/invoice/' + page.value + '/Paid/'+selectedYear.value+'/null/null';

      if (routeParams.status == 'unpaid') {
        endpoint = '/invoice/' + page.value + '/Unpaid/'+selectedYear.value+'/null/null';
      } else if (routeParams.status == 'proforma') {
        endpoint = '/invoice/' + page.value + '/Proforma/'+selectedYear.value+'/null/null';
      }

      const response = await instance.get(endpoint);
      items.value = response.data.Result.data;
      totalItems.value = response.data.Result.totalCount; // Assuming the total item count is returned by the API
    };

    // Watch for changes in route parameters
    watch(
      () => route.params,
      async() => {
        page.value = 1; // Reset to first page on route change
        await fetchItems();
      },
      { immediate: true } // Call immediately on component mount
    );

    const openCreateDialog = () => {
      isEdit.value = false;
      itemForm.value = {
        _id: 0,
        userId: null,
        items: [{ id: 100, name: '', amount: 0, quantity: 0 }], // Array to hold multiple selected products
        total: 0,
        amountPayable: 0,
        taxAmount: 0,
        taxType: '',
        isTaxable: false,
        companyId: 0,
        isProforma: false,
        isNotes: false,
        isPaid: false,
        billDate: new Date(),
        addSignature: false,
        isConsolidated: false
      };
      dialog.value = true;
    };

    const openEditDialog = (item: any) => {
      isEdit.value = true;
      itemForm.value = { ...item };
      dialog.value = true;
    };

    const saveItem = async () => {
      if (isEdit.value) {
        // Update item in items
        const index = items.value.findIndex((item) => item._id === itemForm.value._id);
        if (index !== -1) {
          items.value[index] = itemForm.value;
          await instance.put('/invoice', itemForm.value)
          await fetchItems();
        }
      } else {
        // Create new item
        console.log(itemForm.value)
        itemForm.value._id = items.value.length + 1; // Simple auto-increment for IDs
        items.value.push(itemForm.value);
        await instance.post('/invoice', itemForm.value)
        await fetchItems();
      }
      dialog.value = false;
    };

    const deleteItem = async (_id: number) => {
      items.value = items.value.filter((item) => item._id !== _id);
      await instance.delete('/invoice/' + _id)
      await fetchItems();
    };

    const changePage = (newPage: number) => {
      if (newPage < 1 || newPage > totalPages.value) return;
      page.value = newPage;
      fetchItems(); // Fetch data for the new page
    };

    // Print invoice (simple method, you can extend for better formatting)
    const printInvoice = async (item: any) => {
      await updateAdminInfo(item.companyId);
      // Open a new window for printing
      const printWindow = window.open('', '', 'width=800,height=600');

      // HTML content for the invoice
      const invoiceHtml = GenerateHTML(item, true)

      // Write the invoice HTML to the new window
      printWindow?.document.write(invoiceHtml);
      printWindow?.document.close();
    };

    const GenerateHTML = (item: any, isPrintable: boolean) => {
      return `
    <html>
      <head>
        <title>${item.isProforma ? 'Proforma ':''}Invoice #${item._id}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            color: #333;
            margin: 0;
            padding: 0;
          }

          .invoice-container {
            max-width: 900px;
            margin-left: 10px;
            padding: 5px;
            border: 1px solid #ddd;
            border-radius: 8px;
            background-color: #fff;
          }

          .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 10px;
              padding-bottom: 10px;
              border-bottom: 2px solid #ddd;
            }

            .logo-container {
              flex: 0 0 auto;
              max-width: 100px; /* Adjust based on logo size */
            }

            .logo {
              width: 100%;
              height: 100%;
            }

            .invoice-info {
              text-align: center;
              flex-grow: 1;
            }

            .invoice-info h1 {
              font-size: 30px;
              color: #4CAF50;
              margin-bottom: 5px;
            }

            .company-info {
              text-align: left;
            }

            .company-info p {
              font-size: 16px;
              color: #333;
              margin: 5px 0;
            }

          .address-section {
            margin-bottom: 5px;
            display: flex;
            justify-content: space-between;
            font-size: 16px;
          }

          .address-section .to {
            width: 60%;
          }

          .address-section h3 {
            font-size: 18px;
            margin-bottom: 8px;
            color: #333;
          }

          .address-section p {
            margin: 5px 0;
          }

          .table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 10px;
          }

          .table th, .table td {
            padding: 12px;
            text-align: left;
            border: 1px solid #ddd;
          }

          .table th {
            background-color: #f4f4f4;
            font-weight: bold;
          }

          .table td {
            background-color: #f9f9f9;
          }


          .footer {
            font-size: 14px;
            color: #777;
            margin-top: 10px;
          }
          
          .invoice-summary {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-top: 10px;
            padding: 20px;
          }

          .account-details{
            width: 40%;  /* Take up almost half of the container each */
          }
          .other-details {
            width: 28%;  /* Take up almost half of the container each */
          }


          .account-details h3 {
            margin-bottom: 5px;
          }

          .account-details p {
            margin: 5px 0;
            font-size: 16px;
          }

          .other-details .total-item {
            justify-content: space-between;
            margin-top:5px;
            margin-bottom: 10px;
            font-size: 16px;
          }

          .other-details .label {
            font-weight: normal;
            color: #333;
          }

          .other-details .amount {
            font-weight: bold;
            color: #333;
          }

          .other-details .total-item.total .label {
            font-weight: bold;
            font-size: 16px;
          }

          .other-details .total-item.total .amount {
            font-weight: bold;
            font-size: 16px;
            color: #4CAF50;  /* Green color for the total */
          }

          .other-details .total-item .label,
          .other-details .total-item .amount {
            padding: 5px;
          }

          .other-details .total-item .amount {
            text-align: left;
          }

          /* Signature Section Styling */
          .signature-section {
            margin-top: 0px;
            text-align: center;
            font-size: 14px;
            line-height: 1;
          }

          .signature-section .signature-image {
            width: 140px;  /* Adjust the width as needed */
            height: auto;   /* Maintain the aspect ratio */
            margin: 0px;
          }

          /* Hide the print button while printing */
          @media print {
            .print-btn {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div class="logo-container">
              <img src="/assets/logo.png" alt="logo" class="logo"/>
            </div>
            <div class="invoice-info">
              <h1>${item.isProforma ? 'Proforma ':''}Invoice #SE/${item._id}</h1>
              <p>Issued on: ${new Date(item.billDate).toLocaleDateString("en-GB")}</p>
            </div>
            <div class="company-info">
              <p><strong>${adminInfo.value?.name}</strong></p>
              <p>${adminInfo.value?.address}</p>
              <p>${adminInfo.value?.address1}</p>
              <p>Email &nbsp; : ${adminInfo?.value?.mail}</p>
              <p>Mobile &nbsp;: ${adminInfo?.value?.mobile}</p>
              ${item.taxType === 'GST' ? `<p>GST &nbsp;&nbsp;&nbsp;&nbsp;: ${adminInfo.value?.gstNumber}</p>` : ''}
              ${item.taxType === 'PAN' ? `<p>PAN &nbsp;&nbsp;&nbsp;&nbsp;: ${adminInfo.value?.panNumber}</p>` : ''}
            </div>
          </div>

          <div class="address-section">
            <div class="to">
              <h3>BILL TO:</h3>
              <p>${item?.name}</p>
              <p>${item?.address}</p>
              <p>${item?.address1}</p>
              ${item?.mail !== '' ? `<p>Email  &nbsp;  : ${item?.mail}</p>` : ''}
              ${item?.mobile !== '' ? `<p>Mobile  : ${item?.mobile}</p>` : ''}
              ${item?.gstNumber !== '' ? `<p>GST &nbsp;&nbsp;  : ${item?.gstNumber}</p>` : ''}
              ${item?.panNumber !== '' ? `<p>PAN &nbsp;&nbsp;  : ${item?.panNumber}</p>` : ''}
            </div>
          </div>

            <table class="table">
              <thead>
              <tr>
              <th style="width:3%">S.No</th>
              <th>Item</th>
              ${!item.isConsolidated ? `<th style="width:7%">Amount</th>` : ``}
              ${!item.isConsolidated ? `<th style="width:3%">Quantity</th>` : ``}
              ${!item.isConsolidated ? `<th style="width:60px">Total</th>` : ``}
              ${item.isConsolidated ? `<th style="width:60px">Total</th>` : ``}
              </tr>
              </thead>
              <tbody>
              ${item.items.map((product: any, index: number) => {
        return `
              <tr>
                <td>${index + 1}</td>
                <td>${item?.description[index]}</td>
                ${!item.isConsolidated ? `<td>${product?.amount}</td>` : ``}
                ${!item.isConsolidated ? `<td>${product?.quantity}</td>` : ``}
                ${!item.isConsolidated ? `<td>${product?.amount * product?.quantity}</td>` : ``}
                ${item.isConsolidated && index === 0 ? `<td rowspan="${item.items.length}">${index === 0 ? item?.total : ''}</td>` : ''}
              </tr>
              `;
      }).join('')}
              </tbody>
            </table>
          

          <div class="invoice-summary">
            <div class="account-details">
              <p><strong>Bank Account Details:</strong></p>
              <p>Account Name&nbsp;&nbsp;&nbsp;&nbsp;: ${adminInfo.value?.bankAccountName}</p>
              <p>Account Number : ${adminInfo.value?.bankAccountNumber}</p>
              <p>IFSC Code &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; : ${adminInfo.value?.ifscCode}</p>
              <p>Bank Name &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;   : ${adminInfo.value?.bankName}</p>
            </div>
            <!-- Signature Section -->
            ${item.addSignature ? `<div class="signature-section">
              ${adminInfo.value?._id == 100 ? `<img src="/assets/Bala.png" alt="Signature" class="signature-image" />` : `<img src="/assets/Preetha.png" alt="Signature" class="signature-image" />`}
              <p>Authorized Signature</p>
            </div>`: ``}
            <div class="other-details">
              <div class="total-item">
                <span class="label">Sub Total</span>
                <span class="amount"> :  ₹${item?.total}</span>
              </div>
              ${item?.isTaxable ? `<div class="total-item">
                <span class="label">Tax(18%)</span>
                <span class="amount"> :  ₹${item?.taxAmount}</span>
              </div>`: ``}
              <div class="total-item total">
                <span class="label"><strong>Total  &nbsp;&nbsp;&nbsp;  </strong></span>
                <span class="amount"><strong> :  ₹${item?.amountPayable}</strong></span>
              </div>
              
            </div>
          </div>

          <div class="footer">
            
            ${item.isNotes?'<p><strong>Notes : </strong>The price provided does not include GST or TDS. It is exclusive of these taxes.</p><br/>':''}
            <p style="text-align:center">Thank you for doing business with us!</p>
          </div>
        </div>

        <!-- The Print Button -->
        <div style="text-align: center;" id="printButton">
          ${isPrintable ? '<button class="print-btn" onclick="window.print()">Print Invoice</button>' : ''}
        </div>
      </body>
    </html>
  `;
    }
    // Send email with invoice (simple example, should use an email API)
    const sendInvoiceEmail = async (item: any) => {
      await updateAdminInfo(item.companyId);
      const emailData = {
        to: item.mail, // Replace with dynamic email
        subject: 'Invoice #' + item._id,
        body: `Your invoice has been generated with a total amount payable of INR.${item.amountPayable}`,
        html: GenerateHTML(item, false)
      };

      try {
        await instance.post('/admin/sendInvoice', emailData);
        alert('Invoice sent successfully');
      } catch (error) {
        console.error('Error sending email:', error);
        alert('Failed to send email');
      }
    };



    const fetchUsers = async () => {
      // Make API call with pagination parameters
      const response = await instance.get('/users');
      users.value = response.data.Result;
    };

    const fetchCompanies = async () => {
      // Make API call with pagination parameters
      const response = await instance.get('/admin/companies');
      companies.value = response.data.Result;
      adminInfo.value = response.data.Result[0];
    };

    const fetchProducts = async () => {
      // Make API call with pagination parameters
      const response = await instance.get('/products');
      products.value = response.data.Result;// Assuming the total item count is returned by the API
    };

    onMounted(async () => {
      await fetchItems();
      await fetchUsers();
      await fetchProducts();
      await fetchCompanies();
    });

    return {
      items,
      dialog,
      isEdit,
      itemForm,
      page,
      totalItems,
      totalPages,
      pageSize,
      openCreateDialog,
      openEditDialog,
      saveItem,
      changePage,
      deleteItem,
      printInvoice,
      sendInvoiceEmail,
      users,
      products,
      companies,
      updateTotalAmount,
      updateAdminInfo,
      authStore,
      selectedProduct,
      selectedQuantity,
      addProductToList,
      removeProductFromList,
      selectedYear,
      availableYears,
    };
  },
});
</script>

<style scoped>
/* General Reset */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body,
html {
  height: 100%;
  font-family: Arial, sans-serif;
}

body {
  display: flex;
  justify-content: center;
  /* Horizontally center content */
  align-items: center;
  /* Vertically center content */
  min-height: 100%;
  flex-direction: column;
}

/* Container for the CRUD UI */
.crud-container {
  width: 100%;
  padding: 4px;
  box-sizing: border-box;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Center align elements inside the container */
}

/* Container to hold buttons in a row */
.button-container {
  display: flex;
  gap: 20px;
  /* Space between buttons */
  align-items: center;
  width: 50%;
  /* Vertically align the buttons if they are different heights */
}

/* Button styles */
.create-btn {
  padding: 10px 20px;
  background-color: #4CAF50;
  color: white;
  border: none;
  cursor: pointer;
  font-size: 16px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

/* Hover effect */
.create-btn:hover {
  background-color: #45a049;
}


/* Table for displaying items */
.crud-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  table-layout: auto;
  /* Auto-resize the columns based on content */
}

.crud-table th,
.crud-table td {
  padding: 10px;
  /* Increased padding for better spacing */
  text-align: left;
  border: 1px solid #ddd;
  word-wrap: break-word;
  /* Allow words to wrap in cells */
  max-width: 500px;
  /* Limit column width to accommodate more content */
}

.crud-table th {
  background-color: #f4f4f4;
}

.crud-table td {
  word-break: break-word;
  /* Break long words and prevent overflow */
}

/* Action buttons in table rows */
.edit-btn,
.delete-btn {
  padding: 5px 10px;
  margin: 0 5px;
  cursor: pointer;
}

.edit-btn {
  background-color: #ffa500;
  color: white;
  border: none;
}

.delete-btn {
  background-color: #f44336;
  color: white;
  border: none;
}

.edit-btn:hover {
  background-color: #e68a00;
}

.delete-btn:hover {
  background-color: #e03131;
}


/* Pagination controls */
.pagination {
  display: flex;
  justify-content: center;
  /* Horizontally center the pagination */
  align-items: center;
  margin-top: 20px;
}

.pagination button {
  padding: 5px 15px;
  margin: 0 10px;
  cursor: pointer;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Dialog Styling */
.dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.dialog-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 60%;
  /* Make the dialog content scrollable */
  max-height: 85%;
  /* Define the max height for the dialog box */
  overflow-y: auto;
  /* Enable vertical scrolling */
}

label {
  display: block;
  margin: 10px;
}

select[multiple] {
  height: 150px;
  /* Adjust height for better multi-select visibility */
  padding: 5px;
}

input,
select {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

button {
  padding: 8px 15px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
}

button:hover {
  background-color: #45a049;
}

.dialog input {
  width: 100%;
  padding: 8px;
  margin-bottom: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* Action buttons inside dialog */
.cancel-btn,
.save-btn {
  padding: 8px 15px;
  background-color: #2196f3;
  color: white;
  border: none;
  cursor: pointer;
}

.cancel-btn:hover {
  background-color: #1e88e5;
}

.save-btn {
  background-color: #4caf50;
}

.save-btn:hover {
  background-color: #45a049;
}

/* Additional button styles for print and email */
.print-btn {
  padding: 5px 10px;
  margin: 0 5px;
  background-color: #2196f3;
  color: white;
  border: none;
  cursor: pointer;
}

.email-btn {
  padding: 5px 10px;
  margin: 0 5px;
  background-color: #ff9800;
  color: white;
  border: none;
  cursor: pointer;
}

.print-btn:hover {
  background-color: #1976d2;
}

.email-btn:hover {
  background-color: #e68900;
}
.year-dropdown {
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 14px;
  width: 30%;
}

</style>
  
